import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    LinearProgress,
    Container,
    Divider,
    Avatar,
    Chip,
    Grid,
    Card,
    CardContent,
    useTheme,
    alpha,
    Stack,
    Link,
    CircularProgress,
    Snackbar,
    Alert,
    IconButton,
    Paper,
    useMediaQuery,
    Fade,
    Zoom,
    MobileStepper
} from '@mui/material';
import SwipeableViews from 'react-swipeable-views';
import { autoPlay } from 'react-swipeable-views-utils';
import {
    DownloadOutlined,
    InsertDriveFileOutlined,
    AccessTimeOutlined,
    CloudDownloadOutlined,
    CheckCircleOutlined,
    WhatsApp,
    BusinessOutlined,
    GroupOutlined,
    MessageOutlined,
    ArrowForwardIosOutlined,
    VerifiedOutlined,
    SpeedOutlined,
    ChatOutlined,
    PictureAsPdfOutlined,
    ArchiveOutlined,
    ImageOutlined,
    FolderZipOutlined,
    ErrorOutlined,
    AutomationOutlined,
    IntegrationInstructionsOutlined,
    ShieldOutlined,
    LockOutlined,
    CalendarMonthOutlined,
    ViewKanbanOutlined,
    SettingsOutlined,
    SupportAgentOutlined,
    TrendingUpOutlined,
    SecurityOutlined,
    Check,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    DevicesOutlined,
    BarChartOutlined,
    StorefrontOutlined,
    MarkChatReadOutlined,
    AddTaskOutlined
} from '@mui/icons-material';
import { useParams } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { api_nocors } from '../../services/api';
import axios from 'axios';
import { ReactComponent as UniChatLight } from "../../assets/icons/UniChatLight.svg";

const AutoPlaySwipeableViews = autoPlay(SwipeableViews);

// Determina o ícone baseado na extensão do arquivo
const getFileTypeIcon = (filename) => {
    if (!filename) return <InsertDriveFileOutlined />;

    const extension = filename.split('.').pop().toLowerCase();

    switch (extension) {
        case 'pdf':
            return <PictureAsPdfOutlined />;
        case 'zip':
        case 'rar':
        case '7z':
            return <FolderZipOutlined />;
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'svg':
            return <ImageOutlined />;
        default:
            return <InsertDriveFileOutlined />;
    }
};

// Slides do carousel de features
const featureSlides = [
    {
        title: "WhatsApp Multiusuário",
        description: "Múltiplos atendentes trabalhando em um único número de WhatsApp para sua empresa.",
        icon: <WhatsApp />,
        color: "#25D366"
    },
    {
        title: "Kanban Integrado",
        description: "Gerencie todos os atendimentos com um quadro Kanban intuitivo para sua equipe.",
        icon: <ViewKanbanOutlined />,
        color: "#3498db"
    },
    {
        title: "Setores Organizados",
        description: "Clientes podem escolher departamentos específicos para atendimento direcionado.",
        icon: <BusinessOutlined />,
        color: "#9b59b6"
    },
    {
        title: "Agenda de Tarefas",
        description: "Calendário interno para gerenciar compromissos e follow-ups com clientes.",
        icon: <CalendarMonthOutlined />,
        color: "#e67e22"
    },
    {
        title: "Análise de Desempenho",
        description: "Dados e métricas em tempo real para otimizar a experiência do cliente.",
        icon: <BarChartOutlined />,
        color: "#1abc9c"
    },
    {
        title: "Multi-dispositivos",
        description: "Acesse de qualquer lugar em qualquer dispositivo sem perder atendimentos.",
        icon: <DevicesOutlined />,
        color: "#34495e"
    }
];

const Download = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const isVerySmall = useMediaQuery('(max-width:360px)');
    const [loading, setLoading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [downloaded, setDownloaded] = useState(false);
    const [fileData, setFileData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloadError, setDownloadError] = useState(null);
    const { mediaId } = useParams();
    const [activeStep, setActiveStep] = useState(0);
    const [countdown, setCountdown] = useState(null);
    const countdownRef = useRef(null);
    const [swiperHeight, setSwiperHeight] = useState(isExtraSmall ? 160 : 180);

    // Cores personalizadas - Esquema de cores inspirado em plataformas de download
    const primaryColor = theme.palette.primary.main;
    const primaryLight = alpha(theme.palette.primary.main, 0.1);
    const primaryDark = theme.palette.primary.dark;
    const secondaryColor = theme.palette.secondary.main;
    const successColor = theme.palette.success.main;
    const successLight = alpha(theme.palette.success.main, 0.1);
    const errorColor = theme.palette.error.main;
    const errorLight = alpha(theme.palette.error.main, 0.1);
    const whatsappColor = '#25D366';
    const whatsappLight = alpha(whatsappColor, 0.1);

    // Gradientes modernos estilo WeTransfer/MediaFire
    const bgGradient = theme.palette.mode === 'dark'
        ? 'linear-gradient(150deg, #121212 0%, #1a1a1a 100%)'
        : 'linear-gradient(150deg, #f5f7fa 0%, #eef2f7 100%)';
    const downloadBgGradient = theme.palette.mode === 'dark'
        ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.01) 100%)'
        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(250, 252, 255, 0.9) 100%)';
    const primaryGradient = `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`;
    const whatsappGradient = `linear-gradient(135deg, ${whatsappColor} 0%, ${alpha(whatsappColor, 0.85)} 100%)`;

    // Sombras elevadas para efeito 3D
    const elevatedShadow = `0 10px 30px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.5 : 0.1)}`;
    const buttonShadow = `0 8px 20px ${alpha(primaryColor, 0.25)}`;
    const cardShadow = `0 10px 20px ${alpha(theme.palette.common.black, 0.07)}`;
    const whatsappButtonShadow = `0 8px 20px ${alpha(whatsappColor, 0.25)}`;

    // Ajustar altura do swiper com base no tamanho da tela
    useEffect(() => {
        if (isVerySmall) {
            setSwiperHeight(140);
        } else if (isExtraSmall) {
            setSwiperHeight(160);
        } else if (isMobile) {
            setSwiperHeight(180);
        } else {
            setSwiperHeight(200);
        }
    }, [isVerySmall, isExtraSmall, isMobile]);

    // Formatar tamanho do arquivo
    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        if (bytes < 1024) return bytes + ' B';
        else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
        else return (bytes / 1048576).toFixed(2) + ' MB';
    };

    // Formatar data
    const formatDate = (dateString) => {
        if (!dateString) return '';

        const options = {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        };
        return new Date(dateString).toLocaleDateString('pt-BR', options);
    };

    // Iniciar o contador regressivo para download automático
    const startCountdown = () => {
        if (downloaded || loading || !fileData?.mediaUrl) return;

        setCountdown(5);
        if (countdownRef.current) clearInterval(countdownRef.current);

        countdownRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(countdownRef.current);
                    handleDownload();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    // Navegar entre slides do carrossel
    const handleStepChange = (step) => {
        setActiveStep(step);
    };

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1 >= featureSlides.length ? 0 : prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1 < 0 ? featureSlides.length - 1 : prevActiveStep - 1);
    };

    // Buscar informações do arquivo
    useEffect(() => {
        if (!mediaId) {
            setIsLoading(false);
            setError('ID do arquivo não especificado');
            return;
        }

        const fetchFileData = async () => {
            try {
                const { data } = await api_nocors.get(`/media/${mediaId}`);
                setFileData(data);
                setIsLoading(false);

                // Atualizar o título da página
                document.title = `Download - ${data.mediaName} | UniChat`;

                // Iniciar countdown após carregar os dados
                setTimeout(() => {
                    startCountdown();
                }, 1500);

            } catch (err) {
                console.error('Erro ao buscar dados do arquivo:', err);
                setError('Não foi possível carregar as informações deste arquivo.');
                setIsLoading(false);
            }
        };

        fetchFileData();

        return () => {
            if (countdownRef.current) {
                clearInterval(countdownRef.current);
            }
        };
    }, [mediaId]);

    // Função para baixar o arquivo diretamente
    const handleDownload = async () => {
        if (!fileData?.mediaUrl) return;

        // Limpar countdown se estiver rodando
        if (countdownRef.current) {
            clearInterval(countdownRef.current);
            setCountdown(null);
        }

        setLoading(true);
        setDownloadProgress(0);
        setDownloadError(null);

        try {
            // Usar axios para fazer o download com acompanhamento de progresso
            const response = await axios({
                url: fileData.mediaUrl,
                method: 'GET',
                responseType: 'blob',
                onDownloadProgress: (progressEvent) => {
                    // Calcular o progresso real do download
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setDownloadProgress(percentCompleted);
                }
            });

            // Criar um blob URL para o arquivo
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);

            // Criar um elemento <a> temporário para iniciar o download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileData.mediaName);
            document.body.appendChild(link);

            // Acionar o download
            link.click();

            // Limpar os recursos
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);

            // Marcar como concluído após o download
            setDownloaded(true);
            setLoading(false);
        } catch (err) {
            console.error('Erro ao baixar o arquivo:', err);
            setDownloadError('Ocorreu um erro ao baixar o arquivo. Por favor, tente novamente.');
            setLoading(false);
        }
    };

    // Função para fechar mensagem de erro
    const handleCloseError = () => {
        setDownloadError(null);
    };

    // Benefícios do UniChat - Dados para os cards
    const unichatFeatures = [
        {
            icon: <WhatsApp sx={{ fontSize: isVerySmall ? 20 : 28 }} />,
            title: "WhatsApp API Oficial",
            subtitle: "Certificado pela Meta",
            color: whatsappColor,
            lightColor: whatsappLight,
        },
        {
            icon: <ViewKanbanOutlined sx={{ fontSize: isVerySmall ? 20 : 28 }} />,
            title: "Kanban Integrado",
            subtitle: "Gestão visual completa",
            color: theme.palette.info.main,
            lightColor: alpha(theme.palette.info.main, 0.15),
        },
        {
            icon: <CalendarMonthOutlined sx={{ fontSize: isVerySmall ? 20 : 28 }} />,
            title: "Agenda Inteligente",
            subtitle: "Calendário de tarefas",
            color: theme.palette.warning.main,
            lightColor: alpha(theme.palette.warning.main, 0.15),
        },
        {
            icon: <GroupOutlined sx={{ fontSize: isVerySmall ? 20 : 28 }} />,
            title: "Multiusuários",
            subtitle: "Equipe ilimitada",
            color: theme.palette.success.main,
            lightColor: alpha(theme.palette.success.main, 0.15),
        },
    ];

    return (
        <Box
            sx={{
                minHeight: '100vh',
                height: '100%', // Changed from 100vh to 100% for better mobile experience
                background: bgGradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'auto', // Changed from 'hidden' to 'auto' to prevent content cut-off
                py: { xs: 3, sm: 4 }, // Added vertical padding for better spacing
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: { xs: '150px', sm: '180px', md: '250px' },
                    background: `linear-gradient(135deg, ${alpha(primaryColor, 0.12)} 0%, ${alpha(primaryColor, 0.04)} 100%)`,
                    zIndex: 0
                }
            }}
        >
            <Helmet>
                <title>{fileData ? `Download - ${fileData.mediaName} | UniChat` : 'UniChat | Download'}</title>
                <meta
                    name="description"
                    content="Download de arquivo compartilhado pelo UniChat - CRM completo para atendimento multicanal no WhatsApp."
                />
            </Helmet>

            {/* Snackbar para exibir erros de download */}
            <Snackbar
                open={!!downloadError}
                autoHideDuration={6000}
                onClose={handleCloseError}
                anchorOrigin={{
                    vertical: isMobile ? 'bottom' : 'top',
                    horizontal: 'center'
                }}
                TransitionComponent={Zoom}
            >
                <Alert
                    onClose={handleCloseError}
                    severity="error"
                    variant="filled"
                    sx={{
                        width: '100%',
                        borderRadius: isMobile ? 2 : 3,
                        boxShadow: elevatedShadow,
                        fontSize: isMobile ? '0.8rem' : 'inherit'
                    }}
                >
                    {downloadError}
                </Alert>
            </Snackbar>

            <Container
                maxWidth={false}
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: { xs: 1.5, sm: 2, md: 4 } // Reduced padding on small screens
                }}
            >
                {/* Layout em grid com download e propaganda - Flexível para mobile */}
                <Fade in={true} timeout={800}>
                    <Grid
                        container
                        spacing={{ xs: 2, sm: 3 }} // Reduced spacing on small screens
                        sx={{
                            width: '100%',
                            maxWidth: '1600px'
                        }}
                    >
                        {/* Seção de Download - Ocupa toda a largura em mobile */}
                        <Grid item xs={12} md={7} lg={8} order={{ xs: 2, md: 1 }}>
                            <Paper
                                elevation={5}
                                sx={{
                                    borderRadius: { xs: 3, md: 4 },
                                    overflow: 'hidden',
                                    background: downloadBgGradient,
                                    backdropFilter: 'blur(10px)',
                                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                    boxShadow: elevatedShadow,
                                    height: { md: '100%' },
                                    maxHeight: {
                                        xs: isVerySmall ? '65vh' : isExtraSmall ? '58vh' : '60vh',
                                        sm: '65vh',
                                        md: '80vh'
                                    },
                                    overflow: 'auto',
                                    '&::-webkit-scrollbar': {
                                        width: '6px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: 'transparent',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: alpha(theme.palette.primary.main, 0.2),
                                        borderRadius: 4,
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        background: alpha(theme.palette.primary.main, 0.3),
                                    }
                                }}
                            >
                                {isLoading ? (
                                    <Box
                                        sx={{
                                            p: { xs: 2.5, sm: 3, md: 4 },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                            minHeight: { xs: 200, sm: 280, md: 350 }
                                        }}
                                    >
                                        <CircularProgress
                                            size={isVerySmall ? 40 : isExtraSmall ? 50 : 60}
                                            thickness={4}
                                            sx={{
                                                mb: 2.5,
                                                color: primaryColor,
                                                '& .MuiCircularProgress-circle': {
                                                    strokeLinecap: 'round'
                                                }
                                            }}
                                        />
                                        <Typography
                                            variant={isVerySmall ? "body1" : "h6"}
                                            fontWeight={500}
                                            color="text.secondary"
                                            textAlign="center"
                                        >
                                            Preparando seu download...
                                        </Typography>
                                    </Box>
                                ) : error ? (
                                    <Box
                                        sx={{
                                            p: { xs: 2.5, sm: 3, md: 4 },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                            minHeight: { xs: 200, sm: 280, md: 350 }
                                        }}
                                    >
                                        <ErrorOutlined
                                            sx={{
                                                fontSize: isVerySmall ? 40 : isExtraSmall ? 50 : 60,
                                                color: errorColor,
                                                mb: 2
                                            }}
                                        />
                                        <Typography
                                            variant={isVerySmall ? "h6" : "h5"}
                                            textAlign="center"
                                            gutterBottom
                                            fontWeight={700}
                                            fontSize={isVerySmall ? "1.1rem" : undefined}
                                        >
                                            {error}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            textAlign="center"
                                            sx={{
                                                maxWidth: 450,
                                                mb: 3,
                                                fontSize: isVerySmall ? "0.875rem" : undefined
                                            }}
                                        >
                                            Verifique se o link está correto ou entre em contato com quem compartilhou este arquivo.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size={isVerySmall ? "medium" : "large"}
                                            onClick={() => window.location.reload()}
                                            sx={{
                                                px: 3,
                                                py: isVerySmall ? 0.75 : 1,
                                                borderRadius: isVerySmall ? 8 : 10,
                                                fontWeight: 600,
                                                background: primaryGradient,
                                                boxShadow: buttonShadow,
                                                fontSize: isVerySmall ? "0.875rem" : undefined,
                                                '&:hover': {
                                                    boxShadow: `0 12px 28px ${alpha(primaryColor, 0.4)}`
                                                }
                                            }}
                                        >
                                            Tentar novamente
                                        </Button>
                                    </Box>
                                ) : fileData && (
                                    <Box>
                                        {loading ? (
                                            <Fade in={loading} timeout={500}>
                                                <Box sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
                                                    <Typography
                                                        variant={isVerySmall ? "h6" : "h5"}
                                                        fontWeight={700}
                                                        gutterBottom
                                                        color="primary"
                                                        fontSize={isVerySmall ? "1.1rem" : undefined}
                                                    >
                                                        Preparando seu arquivo
                                                    </Typography>
                                                    <Typography
                                                        variant="body1"
                                                        color="text.secondary"
                                                        paragraph
                                                        fontSize={isVerySmall ? "0.875rem" : undefined}
                                                    >
                                                        Processando "{fileData.mediaName}" para download seguro.
                                                    </Typography>

                                                    <Box sx={{ my: { xs: 2, sm: 3 } }}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={downloadProgress}
                                                            sx={{
                                                                height: isVerySmall ? 8 : 10,
                                                                borderRadius: 10,
                                                                backgroundColor: alpha(primaryColor, 0.15),
                                                                '.MuiLinearProgress-bar': {
                                                                    borderRadius: 10,
                                                                    background: primaryGradient
                                                                }
                                                            }}
                                                        />
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                mt: 1,
                                                                alignItems: 'center'
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                color="text.primary"
                                                                fontWeight={600}
                                                                fontSize={isVerySmall ? "0.75rem" : undefined}
                                                            >
                                                                {Math.round(downloadProgress)}% concluído
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                color="primary"
                                                                fontWeight={600}
                                                                fontSize={isVerySmall ? "0.75rem" : undefined}
                                                            >
                                                                {formatFileSize(fileData.mediaSize)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    <Paper
                                                        elevation={2}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            bgcolor: alpha(primaryColor, 0.08),
                                                            p: isVerySmall ? 1.5 : 2,
                                                            borderRadius: isVerySmall ? 2 : 3,
                                                            border: `1px solid ${alpha(primaryColor, 0.15)}`
                                                        }}
                                                    >
                                                        <Avatar
                                                            variant="rounded"
                                                            sx={{
                                                                width: isVerySmall ? 40 : 50,
                                                                height: isVerySmall ? 40 : 50,
                                                                borderRadius: 2,
                                                                bgcolor: primaryLight,
                                                                color: primaryColor,
                                                                mr: isVerySmall ? 1.5 : 2
                                                            }}
                                                        >
                                                            {getFileTypeIcon(fileData.mediaName)}
                                                        </Avatar>
                                                        <Box sx={{ overflow: 'hidden' }}>
                                                            <Typography
                                                                variant={isVerySmall ? "body1" : "subtitle1"}
                                                                noWrap
                                                                sx={{
                                                                    maxWidth: '100%',
                                                                    fontWeight: 600,
                                                                    fontSize: isVerySmall ? "0.9rem" : undefined
                                                                }}
                                                            >
                                                                {fileData.mediaName}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                fontSize={isVerySmall ? "0.75rem" : undefined}
                                                            >
                                                                {formatDate(fileData.createdAt)}
                                                            </Typography>
                                                        </Box>
                                                    </Paper>
                                                </Box>
                                            </Fade>
                                        ) : downloaded ? (
                                            <Fade in={downloaded} timeout={800}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        p: { xs: 2.5, sm: 3, md: 4 },
                                                    }}
                                                >
                                                    <CheckCircleOutlined sx={{
                                                        color: successColor,
                                                        fontSize: isVerySmall ? 50 : isExtraSmall ? 60 : 70,
                                                        mb: isVerySmall ? 1.5 : 2
                                                    }} />

                                                    <Typography
                                                        variant={isVerySmall ? "h5" : "h4"}
                                                        fontWeight={800}
                                                        gutterBottom
                                                        color="success.main"
                                                        fontSize={isVerySmall ? "1.5rem" : undefined}
                                                        textAlign="center"
                                                    >
                                                        Download Concluído!
                                                    </Typography>

                                                    <Typography
                                                        variant={isVerySmall ? "body2" : "subtitle1"}
                                                        color="text.secondary"
                                                        sx={{
                                                            mb: { xs: 2, sm: 3 },
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        Arquivo salvo na pasta de downloads.
                                                    </Typography>

                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size={isVerySmall ? "medium" : "large"}
                                                        startIcon={<CloudDownloadOutlined />}
                                                        onClick={handleDownload}
                                                        sx={{
                                                            px: { xs: 2, sm: 3 },
                                                            py: isVerySmall ? 1 : 1.3,
                                                            borderRadius: isVerySmall ? 2.5 : 3,
                                                            boxShadow: buttonShadow,
                                                            background: primaryGradient,
                                                            fontWeight: 600,
                                                            fontSize: isVerySmall ? "0.9rem" : undefined,
                                                            '&:hover': {
                                                                boxShadow: `0 12px 28px ${alpha(primaryColor, 0.4)}`
                                                            }
                                                        }}
                                                    >
                                                        Baixar Novamente
                                                    </Button>
                                                </Box>
                                            </Fade>
                                        ) : (
                                            <Fade in={!loading && !downloaded} timeout={800}>
                                                <Box sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            mb: { xs: 2, sm: 3 },
                                                            gap: isVerySmall ? 1.5 : 2,
                                                            flexDirection: isVerySmall ? 'column' : 'row' // Stack vertically on very small screens
                                                        }}
                                                    >
                                                        <Avatar
                                                            variant="rounded"
                                                            sx={{
                                                                bgcolor: primaryLight,
                                                                color: primaryColor,
                                                                width: { xs: 50, sm: 60, md: 70 },
                                                                height: { xs: 50, sm: 60, md: 70 },
                                                                borderRadius: 3,
                                                                flexShrink: 0,
                                                                boxShadow: `0 5px 15px ${alpha(primaryColor, 0.2)}`,
                                                                mb: isVerySmall ? 1.5 : 0 // Bottom margin only for vertical layout
                                                            }}
                                                        >
                                                            {getFileTypeIcon(fileData.mediaName)}
                                                        </Avatar>

                                                        <Box sx={{ overflow: 'hidden', width: isVerySmall ? '100%' : 'auto' }}>
                                                            <Typography
                                                                variant={isVerySmall ? "h6" : "h5"}
                                                                component="h1"
                                                                fontWeight={700}
                                                                textAlign={isVerySmall ? "center" : "left"}
                                                                sx={{
                                                                    wordBreak: 'break-word',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    color: theme.palette.text.primary,
                                                                    lineHeight: 1.2,
                                                                    fontSize: isVerySmall ? "1.1rem" : undefined
                                                                }}
                                                            >
                                                                {fileData.mediaName}
                                                            </Typography>

                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flexWrap: 'wrap',
                                                                    gap: 1,
                                                                    mt: 1,
                                                                    justifyContent: isVerySmall ? 'center' : 'flex-start' // Center chips on very small screens
                                                                }}
                                                            >
                                                                <Chip
                                                                    size="small"
                                                                    label={formatFileSize(fileData.mediaSize)}
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        bgcolor: primaryLight,
                                                                        color: primaryColor,
                                                                        borderRadius: 3,
                                                                        height: isVerySmall ? 22 : 24,
                                                                        fontSize: isVerySmall ? "0.7rem" : undefined
                                                                    }}
                                                                />

                                                                <Chip
                                                                    size="small"
                                                                    label={fileData.mediaName.split('.').pop().toUpperCase()}
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        bgcolor: alpha(theme.palette.grey[600], 0.1),
                                                                        color: theme.palette.text.primary,
                                                                        borderRadius: 3,
                                                                        height: isVerySmall ? 22 : 24,
                                                                        fontSize: isVerySmall ? "0.7rem" : undefined
                                                                    }}
                                                                />

                                                                <Chip
                                                                    size="small"
                                                                    icon={isVerySmall ? null : <SecurityOutlined sx={{ fontSize: '0.9rem !important' }} />}
                                                                    label="Verificado"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        bgcolor: alpha(successColor, 0.15),
                                                                        color: successColor,
                                                                        borderRadius: 3,
                                                                        height: isVerySmall ? 22 : 24,
                                                                        fontSize: isVerySmall ? "0.7rem" : undefined
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Box>

                                                    <Typography
                                                        variant={isVerySmall ? "caption" : "body2"}
                                                        color="text.secondary"
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            mb: { xs: 2, sm: 3 },
                                                            fontWeight: 500,
                                                            justifyContent: isVerySmall ? 'center' : 'flex-start' // Center on very small screens
                                                        }}
                                                    >
                                                        <AccessTimeOutlined sx={{
                                                            mr: 1,
                                                            fontSize: isVerySmall ? 14 : 16
                                                        }} />
                                                        Enviado em {formatDate(fileData.createdAt)}
                                                    </Typography>

                                                    <Divider sx={{ mb: { xs: 2, sm: 3 }, borderColor: alpha(theme.palette.divider, 0.12) }} />

                                                    <Box sx={{ mb: 1 }}>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                mb: { xs: 2, sm: 3 },
                                                                p: isVerySmall ? 1.5 : 2,
                                                                borderRadius: isVerySmall ? 2 : 3,
                                                                bgcolor: successLight,
                                                                border: `1px solid ${alpha(successColor, 0.2)}`
                                                            }}
                                                        >
                                                            <SecurityOutlined
                                                                color="success"
                                                                sx={{
                                                                    mr: 1.5,
                                                                    fontSize: isVerySmall ? 18 : 20
                                                                }}
                                                            />
                                                            <Typography
                                                                variant={isVerySmall ? "caption" : "body2"}
                                                                color="success.main"
                                                                fontWeight={600}
                                                            >
                                                                Arquivo verificado e seguro para download
                                                            </Typography>
                                                        </Box>

                                                        {/* Botão estilo MediaFire/WeTransfer com contador regressivo */}
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                position: 'relative',
                                                            }}
                                                        >
                                                            {countdown && (
                                                                <Typography
                                                                    variant={isVerySmall ? "caption" : "body2"}
                                                                    color="text.secondary"
                                                                    sx={{
                                                                        mb: 1.5,
                                                                        fontWeight: 500
                                                                    }}
                                                                >
                                                                    Download em {countdown} {countdown === 1 ? 'segundo' : 'segundos'}
                                                                </Typography>
                                                            )}

                                                            <Button
                                                                variant="contained"
                                                                color="primary"
                                                                size={isVerySmall ? "medium" : "large"}
                                                                fullWidth
                                                                disableElevation
                                                                startIcon={<CloudDownloadOutlined sx={{
                                                                    fontSize: isVerySmall ? 18 : 24
                                                                }} />}
                                                                onClick={handleDownload}
                                                                sx={{
                                                                    py: isVerySmall ? 1.2 : 1.5,
                                                                    borderRadius: isVerySmall ? 2.5 : 3,
                                                                    boxShadow: buttonShadow,
                                                                    background: primaryGradient,
                                                                    fontWeight: 700,
                                                                    fontSize: {
                                                                        xs: isVerySmall ? '0.9rem' : '1rem',
                                                                        md: '1.1rem'
                                                                    },
                                                                    letterSpacing: '0.02em',
                                                                    transition: 'all 0.3s ease',
                                                                    position: 'relative',
                                                                    overflow: 'hidden',
                                                                    '&:hover': {
                                                                        transform: 'translateY(-3px)',
                                                                        boxShadow: `0 12px 30px ${alpha(primaryColor, 0.4)}`
                                                                    },
                                                                    '&::after': countdown ? {
                                                                        content: '""',
                                                                        position: 'absolute',
                                                                        bottom: 0,
                                                                        left: 0,
                                                                        height: '4px',
                                                                        width: `${(countdown / 5) * 100}%`,
                                                                        background: alpha('#fff', 0.3)
                                                                    } : {}
                                                                }}
                                                            >
                                                                Baixar Arquivo Agora
                                                            </Button>

                                                            <Box
                                                                sx={{
                                                                    mt: 1.5,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    textAlign: 'center'
                                                                }}
                                                            >
                                                                <LockOutlined sx={{
                                                                    fontSize: isVerySmall ? 10 : 12,
                                                                    mr: 0.5,
                                                                    color: 'text.secondary'
                                                                }} />
                                                                <Typography
                                                                    variant={isVerySmall ? "caption" : "caption"}
                                                                    color="text.secondary"
                                                                    fontSize={isVerySmall ? "0.65rem" : undefined}
                                                                >
                                                                    Protegido pela tecnologia UniChat Shield™
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Fade>
                                        )}
                                    </Box>
                                )}
                            </Paper>
                        </Grid>

                        {/* Seção de Anúncio - Mobile em cima, Desktop ao lado */}
                        <Grid item xs={12} md={5} lg={4} order={{ xs: 1, md: 2 }}>
                            {!isLoading && !error && (
                                <Fade in={true} timeout={1000}>
                                    <Paper
                                        elevation={3}
                                        sx={{
                                            borderRadius: { xs: 3, md: 4 },
                                            overflow: 'hidden',
                                            background: theme.palette.mode === 'dark'
                                                ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
                                                : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
                                            backdropFilter: 'blur(10px)',
                                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                            boxShadow: cardShadow,
                                            height: '100%',
                                            maxHeight: {
                                                xs: isVerySmall ? '38vh' : '40vh',
                                                sm: '45vh',
                                                md: '80vh'
                                            },
                                            overflow: 'auto',
                                            position: 'relative',
                                            '&::-webkit-scrollbar': {
                                                width: '6px',
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                background: 'transparent',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                background: alpha(theme.palette.primary.main, 0.2),
                                                borderRadius: 4,
                                            },
                                            '&::-webkit-scrollbar-thumb:hover': {
                                                background: alpha(theme.palette.primary.main, 0.3),
                                            }
                                        }}
                                    >
                                        {/* Badge "Anúncio" */}
                                        <Chip
                                            label="Anúncio"
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: 6,
                                                right: 6,
                                                zIndex: 2,
                                                bgcolor: alpha(theme.palette.grey[600], 0.1),
                                                color: theme.palette.text.secondary,
                                                fontWeight: 500,
                                                fontSize: '0.65rem',
                                                height: isVerySmall ? 18 : 20,
                                                borderRadius: 1
                                            }}
                                        />

                                        {/* Logo e título - Header */}
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            py: { xs: 1.5, sm: 2 },
                                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                                        }}>
                                            <UniChatLight
                                                width={isVerySmall ? 30 : 40}
                                                height={isVerySmall ? 30 : 40}
                                            />
                                            <Typography
                                                variant={isVerySmall ? "h6" : "h5"}
                                                fontWeight={800}
                                                sx={{
                                                    color: whatsappColor,
                                                    letterSpacing: '-0.02em',
                                                    textShadow: `0 2px 4px ${alpha(whatsappColor, 0.2)}`,
                                                    fontSize: isVerySmall ? "1.15rem" : undefined
                                                }}
                                            >
                                                UniChat
                                            </Typography>
                                        </Box>

                                        {/* Conteúdo compactado */}
                                        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                                            {/* Mini carrossel de recursos */}
                                            <Box sx={{
                                                position: 'relative',
                                                borderRadius: isVerySmall ? 2 : 3,
                                                overflow: 'hidden',
                                                boxShadow: `0 5px 15px ${alpha(theme.palette.common.black, 0.05)}`,
                                                border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                                                bgcolor: theme.palette.mode === 'dark'
                                                    ? alpha(theme.palette.background.paper, 0.6)
                                                    : alpha(theme.palette.background.paper, 0.6),
                                                height: {
                                                    xs: swiperHeight,
                                                    md: 200
                                                },
                                                mb: 1
                                            }}>
                                                <AutoPlaySwipeableViews
                                                    axis="x"
                                                    index={activeStep}
                                                    onChangeIndex={handleStepChange}
                                                    enableMouseEvents
                                                    interval={4000}
                                                >
                                                    {featureSlides.map((slide, index) => (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                p: { xs: 1.5, sm: 2 },
                                                                height: {
                                                                    xs: swiperHeight,
                                                                    md: 200
                                                                }
                                                            }}
                                                        >
                                                            <Avatar
                                                                sx={{
                                                                    width: isVerySmall ? 40 : isExtraSmall ? 50 : 60,
                                                                    height: isVerySmall ? 40 : isExtraSmall ? 50 : 60,
                                                                    bgcolor: alpha(slide.color, 0.15),
                                                                    color: slide.color,
                                                                    mb: isVerySmall ? 1 : 2
                                                                }}
                                                            >
                                                                {slide.icon}
                                                            </Avatar>
                                                            <Typography
                                                                variant={isVerySmall ? "subtitle1" : "h6"}
                                                                fontWeight={700}
                                                                textAlign="center"
                                                                sx={{
                                                                    mb: 0.5,
                                                                    fontSize: isVerySmall ? "0.95rem" : undefined
                                                                }}
                                                            >
                                                                {slide.title}
                                                            </Typography>
                                                            <Typography
                                                                variant={isVerySmall ? "caption" : "body2"}
                                                                textAlign="center"
                                                                color="text.secondary"
                                                                sx={{
                                                                    maxWidth: 350,
                                                                    mx: 'auto',
                                                                    fontSize: isVerySmall ? "0.7rem" : undefined,
                                                                    lineHeight: isVerySmall ? 1.3 : undefined
                                                                }}
                                                            >
                                                                {slide.description}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </AutoPlaySwipeableViews>
                                            </Box>

                                            <MobileStepper
                                                steps={featureSlides.length}
                                                position="static"
                                                activeStep={activeStep}
                                                sx={{
                                                    bgcolor: 'transparent',
                                                    mb: isVerySmall ? 1 : 2,
                                                    p: 0,
                                                    '& .MuiMobileStepper-dot': {
                                                        width: isVerySmall ? 5 : 6,
                                                        height: isVerySmall ? 5 : 6,
                                                        mx: 0.5
                                                    },
                                                    '& .MuiMobileStepper-dotActive': {
                                                        bgcolor: whatsappColor
                                                    }
                                                }}
                                                nextButton={
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleNext}
                                                        sx={{
                                                            bgcolor: alpha(theme.palette.grey[500], 0.1),
                                                            width: isVerySmall ? 20 : 24,
                                                            height: isVerySmall ? 20 : 24,
                                                            '&:hover': {
                                                                bgcolor: alpha(theme.palette.grey[500], 0.2),
                                                            }
                                                        }}
                                                    >
                                                        <KeyboardArrowRight
                                                            sx={{ fontSize: isVerySmall ? '0.9rem' : 'small' }}
                                                        />
                                                    </IconButton>
                                                }
                                                backButton={
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleBack}
                                                        sx={{
                                                            bgcolor: alpha(theme.palette.grey[500], 0.1),
                                                            width: isVerySmall ? 20 : 24,
                                                            height: isVerySmall ? 20 : 24,
                                                            '&:hover': {
                                                                bgcolor: alpha(theme.palette.grey[500], 0.2),
                                                            }
                                                        }}
                                                    >
                                                        <KeyboardArrowLeft
                                                            sx={{ fontSize: isVerySmall ? '0.9rem' : 'small' }}
                                                        />
                                                    </IconButton>
                                                }
                                            />

                                            {/* Recursos compactos - Em Grid para mobile */}
                                            {!isVerySmall && (
                                                <Typography
                                                    variant={isVerySmall ? "caption" : "subtitle2"}
                                                    fontWeight={700}
                                                    sx={{ mb: isVerySmall ? 0.75 : 1.5 }}
                                                >
                                                    Recursos Principais:
                                                </Typography>
                                            )}

                                            <Grid container spacing={1} sx={{ mb: isVerySmall ? 1.5 : 2.5 }}>
                                                {[
                                                    ["WhatsApp API Oficial", <WhatsApp fontSize={isVerySmall ? "inherit" : "small"} sx={{ fontSize: isVerySmall ? 14 : undefined }} />],
                                                    ["Múltiplos atendentes", <GroupOutlined fontSize={isVerySmall ? "inherit" : "small"} sx={{ fontSize: isVerySmall ? 14 : undefined }} />],
                                                    ["Kanban integrado", <ViewKanbanOutlined fontSize={isVerySmall ? "inherit" : "small"} sx={{ fontSize: isVerySmall ? 14 : undefined }} />],
                                                    ["Agenda interna", <CalendarMonthOutlined fontSize={isVerySmall ? "inherit" : "small"} sx={{ fontSize: isVerySmall ? 14 : undefined }} />],
                                                ].map(([text, icon], idx) => (
                                                    <Grid item xs={6} key={idx}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            p: isVerySmall ? 0.75 : 1,
                                                            borderRadius: isVerySmall ? 1.5 : 2,
                                                            bgcolor: alpha(theme.palette.background.default, 0.5),
                                                            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                                                        }}>
                                                            <Avatar
                                                                sx={{
                                                                    width: isVerySmall ? 18 : 24,
                                                                    height: isVerySmall ? 18 : 24,
                                                                    mr: isVerySmall ? 0.5 : 1,
                                                                    bgcolor: alpha(whatsappColor, 0.1),
                                                                    color: whatsappColor
                                                                }}
                                                            >
                                                                {icon}
                                                            </Avatar>
                                                            <Typography
                                                                variant="caption"
                                                                fontWeight={600}
                                                                noWrap
                                                                fontSize={isVerySmall ? "0.65rem" : undefined}
                                                            >
                                                                {text}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                ))}
                                            </Grid>

                                            {/* CTA */}
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                startIcon={<WhatsApp sx={{ fontSize: isVerySmall ? 16 : undefined }} />}
                                                href="https://wa.me/556391000057?text=Olá,%20gostaria%20de%20conhecer%20o%20UniChat"
                                                target="_blank"
                                                rel="noopener"
                                                sx={{
                                                    py: isVerySmall ? 1 : { xs: 1.2, md: 1.5 },
                                                    borderRadius: isVerySmall ? 1.5 : 2,
                                                    fontWeight: 700,
                                                    mb: isVerySmall ? 0.5 : 1,
                                                    background: whatsappGradient,
                                                    boxShadow: whatsappButtonShadow,
                                                    transition: 'all 0.3s ease',
                                                    fontSize: isVerySmall ? "0.8rem" : undefined,
                                                    '&:hover': {
                                                        transform: 'translateY(-3px)',
                                                        boxShadow: `0 10px 20px ${alpha(whatsappColor, 0.3)}`
                                                    }
                                                }}
                                            >
                                                Solicitar Demonstração
                                            </Button>

                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    display: 'block',
                                                    textAlign: 'center',
                                                    fontWeight: 500,
                                                    fontSize: isVerySmall ? "0.65rem" : undefined
                                                }}
                                            >
                                                Revolucione seu atendimento hoje
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Fade>
                            )}
                        </Grid>
                    </Grid>
                </Fade>
            </Container>
        </Box>
    );
};

export default Download;